/**
 * React Query hooks for the subscription-plan surface.
 *
 * Covers:
 *   - Plan + entitlement snapshot (cached in sync with the BFF).
 *   - Live pricing per tier (driven by SuperAdmin-configurable catalog).
 *   - Current plan-change request (PENDING / APPROVED / REJECTED / CANCELLED).
 *   - Mutations: request change, cancel request.
 *
 * Everything here is keyed on tenantId + an explicit access-token scope
 * handled by the BFF; the React layer never sees an INTERNAL_* secret.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient as api } from '@services/api-client';

// Keep in sync with centraOnePlus-shared/src/types/tenant.types.ts.
export type PlanTier = 'free' | 'growth' | 'professional';

export interface FeatureLabel {
  id: string;
  name: string;
  description: string;
  category: string;
}

export interface EntitlementSnapshot {
  plan: PlanTier;
  features: string[];
  featureLabels: FeatureLabel[];
  limits: Record<string, number>;
}

export interface EffectivePriceRow {
  tier: PlanTier;
  currency: string;
  monthlyAmount: string;
  annualAmount: string;
  effectiveFrom: string;
}

export interface TierCatalog {
  tier: PlanTier;
  features: string[];
  featureLabels: FeatureLabel[];
  limits: Record<string, number>;
}

export interface LimitLabel {
  key: string;
  name: string;
  unit: string;
}

export interface PublicPricing {
  currency: string;
  taxMode: 'inclusive' | 'exclusive' | 'disabled';
  annualDiscountPercent: number;
  prices: EffectivePriceRow[];
  tiers: TierCatalog[];
  /**
   * Display copy for every numeric limit key. Server-shipped reference
   * data; frontend uses it for upgrade-card "what's new" deltas. Older
   * tenant-service builds don't ship this — `usePublicPricing` defaults
   * to `{}` so callers that don't need the catalog stay unaffected.
   */
  limitLabels: Record<string, LimitLabel>;
}

export type PlanChangeRequestStatus =
  | 'PENDING'
  | 'APPROVED'
  | 'REJECTED'
  | 'CANCELLED';

export type BillingCycle = 'MONTHLY' | 'YEARLY';

export interface PlanChangeRequest {
  id: string;
  tenantId: string;
  fromPlan: PlanTier;
  toPlan: PlanTier;
  status: PlanChangeRequestStatus;
  /**
   * Org-admin's chosen billing cycle. Server-side default is 'MONTHLY'
   * for any pre-migration row, so the field is non-null on new rows.
   */
  billingCycle: BillingCycle;
  requestedByUserId: string | null;
  requestedAt: string;
  approvedByPlatformUserId: string | null;
  approvedAt: string | null;
  rejectedReason: string | null;
  contractRef: string | null;
  notes: string | null;
}

export interface UsageSnapshot {
  agents: number;
  ticketsThisMonth: number;
  activeAutomationRules: number;
  customFields: number;
  activeApiKeys: number;
  pipelines: number;
  ticketTypes: number;
  kbArticles: number;
}

const QK = {
  entitlements: (tid: string | null | undefined) => ['plan', 'entitlements', tid],
  pricing: (currency?: string) => ['plan', 'pricing', currency ?? 'default'],
  currentRequest: (tid: string | null | undefined) => [
    'plan',
    'current-request',
    tid,
  ],
  usage: (tid: string | null | undefined) => ['plan', 'usage', tid],
};

export function useEntitlements(tenantId: string | null | undefined) {
  return useQuery({
    queryKey: QK.entitlements(tenantId),
    queryFn: async (): Promise<EntitlementSnapshot> => {
      const res = await api.get(`/subscriptions/tenant/${tenantId}/entitlements`);
      // Default `featureLabels` to an empty array so a stale tenant-service
      // pod that hasn't picked up the new shared package yet still renders
      // (older deployments returned only `features` + `limits`). The current
      // server fills this in.
      const data = res.data ?? {};
      return {
        ...data,
        featureLabels: data.featureLabels ?? [],
      };
    },
    enabled: Boolean(tenantId),
    staleTime: 30_000,
  });
}

export function usePublicPricing(currency?: string) {
  return useQuery({
    queryKey: QK.pricing(currency),
    queryFn: async (): Promise<PublicPricing> => {
      const res = await api.get('/subscriptions/pricing', {
        params: currency ? { currency } : undefined,
      });
      // Same backward-compat default as useEntitlements: a stale
      // tenant-service that pre-dates the catalog change returns only
      // { currency, taxMode, annualDiscountPercent, prices }. The new
      // server adds `tiers` and `limitLabels`. Default both to empty
      // values so the upgrade cards just hide their feature/limit
      // diffs instead of crashing.
      const data = res.data ?? {};
      return {
        ...data,
        tiers: data.tiers ?? [],
        limitLabels: data.limitLabels ?? {},
      };
    },
    staleTime: 5 * 60_000,
  });
}

export function useCurrentPlanChangeRequest(tenantId: string | null | undefined) {
  return useQuery({
    queryKey: QK.currentRequest(tenantId),
    queryFn: async (): Promise<PlanChangeRequest | null> => {
      try {
        const res = await api.get(
          `/subscriptions/tenant/${tenantId}/plan-change-request/current`,
        );
        return res.data ?? null;
      } catch (err: any) {
        if (err?.response?.status === 404) return null;
        throw err;
      }
    },
    enabled: Boolean(tenantId),
    staleTime: 10_000,
  });
}

export function useRequestPlanChange(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: {
      fromPlan: PlanTier;
      toPlan: PlanTier;
      billingCycle: BillingCycle;
    }): Promise<PlanChangeRequest> => {
      const res = await api.post(
        `/subscriptions/tenant/${tenantId}/plan-change-request`,
        body,
      );
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.currentRequest(tenantId) });
      qc.invalidateQueries({ queryKey: QK.entitlements(tenantId) });
    },
  });
}

export interface TenantSubscription {
  id: string;
  tenantId: string;
  plan: PlanTier;
  status: 'active' | 'trialing' | 'past_due' | 'cancelled' | 'suspended';
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  trialEndsAt: string | null;
  trialFallbackPlan: PlanTier | null;
  pastDueSince: string | null;
  gracePeriodEndsAt: string | null;
  pricingSnapshot: {
    currency: string;
    monthlyAmount: string;
    annualAmount: string;
    interval: 'monthly' | 'annual';
    taxMode: 'inclusive' | 'exclusive' | 'disabled';
    snapshotAt: string;
  } | null;
}

export function useTenantSubscription(tenantId: string | null | undefined) {
  return useQuery({
    queryKey: ['plan', 'subscription', tenantId],
    queryFn: async (): Promise<TenantSubscription | null> => {
      try {
        const res = await api.get(`/subscriptions/tenant/${tenantId}`);
        return res.data ?? null;
      } catch (err: any) {
        if (err?.response?.status === 404) return null;
        throw err;
      }
    },
    enabled: Boolean(tenantId),
    staleTime: 15_000,
  });
}

export function usePlanHistory(tenantId: string | null | undefined) {
  return useQuery({
    queryKey: ['plan', 'history', tenantId],
    queryFn: async (): Promise<PlanChangeRequest[]> => {
      const res = await api.get(
        `/subscriptions/tenant/${tenantId}/plan-history`,
      );
      return res.data || [];
    },
    enabled: Boolean(tenantId),
    staleTime: 30_000,
  });
}

export function useCancelSubscription(subscriptionId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { reason?: string; immediate?: boolean }) => {
      const res = await api.post(
        `/subscriptions/${subscriptionId}/cancel-by-owner`,
        body,
      );
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['plan'] });
    },
  });
}

export function useValidatePromoCode(tenantId: string) {
  return useMutation({
    mutationFn: async (params: {
      code: string;
      targetPlan?: string;
    }): Promise<{
      id: string;
      code: string;
      kind: string;
      percentOff: number | null;
      amountOff: string | null;
      currency: string | null;
      overrideMonthlyAmount: string | null;
      overrideAnnualAmount: string | null;
      trialExtensionDays: number | null;
      description: string | null;
    }> => {
      const res = await api.get(
        `/subscriptions/tenant/${tenantId}/promo-codes/${encodeURIComponent(params.code)}/validate`,
        { params: { targetPlan: params.targetPlan } },
      );
      return res.data;
    },
  });
}

export function useTenantUsage(tenantId: string | null | undefined) {
  return useQuery({
    queryKey: QK.usage(tenantId),
    queryFn: async (): Promise<UsageSnapshot> => {
      const res = await api.get(`/subscriptions/tenant/${tenantId}/usage`);
      return res.data;
    },
    enabled: Boolean(tenantId),
    staleTime: 60_000,
  });
}

export function useCancelPlanChangeRequest(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (requestId: string): Promise<PlanChangeRequest> => {
      const res = await api.delete(
        `/subscriptions/tenant/${tenantId}/plan-change-request/${requestId}`,
      );
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.currentRequest(tenantId) });
    },
  });
}

/**
 * Tiny helper that returns `{ ok, warn }` verdicts mirroring the
 * server-side `EntitlementsService.checkUnderLimit()`. Renders the
 * UsageMeter color bands.
 */
export function computeUsageVerdict(
  current: number,
  limit: number,
): { ok: boolean; warn: boolean; percent: number } {
  if (limit === -1) return { ok: true, warn: false, percent: 0 };
  if (limit <= 0) return { ok: current <= 0, warn: false, percent: 100 };
  const percent = Math.min(100, Math.round((current / limit) * 100));
  const hardLimit = Math.floor(limit * 1.1);
  return { ok: current <= hardLimit, warn: current >= Math.floor(limit * 0.8), percent };
}
